import {Injectable} from '@angular/core';
import {BehaviorSubject, distinctUntilChanged, Observable} from 'rxjs';
import {TabNavigationService} from '../../../../micromate-learn-lib/src/lib/services/TabNavigationService';
import {ActivatedRoute, ActivatedRouteSnapshot, NavigationEnd, NavigationExtras, Params, Router} from '@angular/router';
import {BotIdService} from '../../../../micromate-learn-lib/src/lib/services/BotIdService';
import {BotatoChatManagerService} from 'botato-angular-lib';
import {environment} from '../../environments/environment';
import {ChatNewMessageIndicatorService} from '../../../../micromate-learn-lib/src/lib/services/ChatNewMessageIndicatorService';
import {filter} from 'rxjs/operators';
import {
    OpenInsightPopupData
} from '../../../../micromate-learn-lib/src/lib/components/study-progress/insight-popup/OpenInsightPopupData';
import {OpenPopupData} from './OpenPopupData';
import {
    InsightPopupTabs
} from '../../../../micromate-learn-lib/src/lib/components/study-progress/insight-popup/InsightPopupTabs';
import {MicrosoftTeamsRecognitionService} from '../profile-pages/MicrosoftTeamsRecognitionService';
import {InsightTabs} from '../../../../micromate-learn-lib/src/lib/components/study-progress/insight-popup/InsightTabs';
import {TimePeriodMode} from '../../../../micromate-learn-lib/src/lib/services/rest/api-types/TimePeriodMode';

@Injectable({
    providedIn: 'root'
})
export class NavigationService extends TabNavigationService {
    private activeNavigation = new BehaviorSubject<boolean>(false);
    public activeNavigation$ = this.activeNavigation.asObservable();

    private isProfileVisible: BehaviorSubject<boolean> = new BehaviorSubject(false);
    public isProfileVisible$: Observable<boolean> = this.isProfileVisible.asObservable();

    private openPopup = new BehaviorSubject<OpenPopupData | undefined>(undefined);
    public openPopup$: Observable<OpenPopupData | undefined> = this.openPopup.asObservable();

    private timePeriodMode: BehaviorSubject<TimePeriodMode> = new BehaviorSubject<TimePeriodMode>(TimePeriodMode.Daily);
    public timePeriodMode$: Observable<TimePeriodMode> = this.timePeriodMode.asObservable().pipe(distinctUntilChanged());

    private isChatConnected = false;

    constructor(private router: Router,
                private activeRoute: ActivatedRoute,
                private chatManager: BotatoChatManagerService,
                private botIdService: BotIdService,
                private chatNewMessageIndicatorService: ChatNewMessageIndicatorService) {
        super();
        this.chatManager.isConnected$.subscribe(connected => this.isChatConnected = connected);
        this.router.events.pipe(filter(event => event instanceof NavigationEnd)).subscribe(() => {
            this.checkIfOpenPopup();
            if (this.isChatTabActive()) {
                this.chatNewMessageIndicatorService.reset();
                this.chatManager.cancelPendingDivider();
                if (this.isChatConnected) {
                    void this.chatManager.sendMarkAsRead();
                }
            } else {
                this.chatManager.removeNewMessagesDivider();
                this.chatManager.markDividerBeforeNextBotMessage();
            }
        });
    }

    private checkIfOpenPopup(): void {
        const shouldPopupBeOpened = this.shouldPopupBeOpened();
        if (shouldPopupBeOpened.shouldBeOpened) {
            this.readTimePeriodMode();
            // eslint-disable-next-line no-null/no-null
            if (shouldPopupBeOpened.snapshot !== undefined && shouldPopupBeOpened.snapshot !== null) {
                this.recogniseLearningProgressInsights(shouldPopupBeOpened.snapshot);
            }
        }
    }

    private readTimePeriodMode(): void {
        const tree = this.router.parseUrl(this.router.url);
        const timePeriodMode = tree.queryParams['timePeriodMode'] ?? TimePeriodMode.Daily;
        if (timePeriodMode !== this.timePeriodMode.getValue()) {
            this.timePeriodMode.next(timePeriodMode);
        }
    }

    private shouldPopupBeOpened(): { shouldBeOpened: boolean, snapshot: ActivatedRouteSnapshot | null } {
        const first = this.activeRoute.snapshot.firstChild!;
        const firstPath = first.url[0]?.path;
        if (MicrosoftTeamsRecognitionService.isRunningWithinMicrosoftTeams()) {
            const isOpened = firstPath !== undefined && firstPath === 'lernfortschritt';
            return {shouldBeOpened: isOpened, snapshot: first.firstChild};
        }

        const second = first.firstChild!;
        const secondPath = second.url[0]?.path;
        const isOpened = firstPath !== undefined && secondPath !== undefined && firstPath === 'learn' && secondPath === 'lernfortschritt';
        return {shouldBeOpened: isOpened, snapshot: second.firstChild};
    }

    private recogniseLearningProgressInsights(snapshot: ActivatedRouteSnapshot): void {
        const learningPackageId = snapshot?.params?.learningPackageId;
        // eslint-disable-next-line no-null/no-null
        const segmentPath = snapshot !== null && snapshot?.url?.length !== undefined ? snapshot.url[snapshot.url.length - 1].path : undefined;
        if (segmentPath === 'insights') {
            const lastSegmentPath = snapshot.firstChild?.url[0].path;
            if (lastSegmentPath !== undefined && learningPackageId !== undefined) {
                this.openPopup.next(new OpenInsightPopupData(learningPackageId, lastSegmentPath as InsightPopupTabs));
            }
        }
    }

    public closeProfile(): void {
        this.isProfileVisible.next(false);
        this.navigateToChat(false);
    }

    public toggleProfileVisible(): void {
        const isProfileVisible = this.isProfileVisible.getValue();
        this.isProfileVisible.next(!isProfileVisible);
    }

    public setActiveNavigation(status: boolean): void {
        this.activeNavigation.next(status);
    }

    public getActiveNavigation(): boolean {
        return this.activeNavigation.value;
    }

    public navigateToBadges(addOpenFromNavigationParameter: boolean): void {
        this.navigate(['learn', 'badges'], addOpenFromNavigationParameter);
    }

    public navigateToChat(addOpenFromNavigationParameter: boolean): void {
        this.navigate(['/learn'], addOpenFromNavigationParameter);
        if (this.isChatConnected) {
            void this.chatManager.sendMarkAsRead();
        }
    }

    public navigateToScoreboard(addOpenFromNavigationParameter: boolean): void {
        this.navigate(['learn', 'rangliste'], addOpenFromNavigationParameter);
    }

    public navigateToStudyProgress(addOpenFromNavigationParameter: boolean): void {
        this.navigate(['learn', 'lernfortschritt'], addOpenFromNavigationParameter);
    }

    public navigateToProfile(addOpenFromNavigationParameter: boolean): void {
        this.navigate(['learn', 'profil'], addOpenFromNavigationParameter);
    }

    private navigate(path: string[], addOpenFromNavigationParameter: boolean, customParams?: {
        [key: string]: unknown
    }): void {
        this.openPopup.next(undefined);
        const parameters: NavigationExtras | undefined = this.buildNavigationExtras(addOpenFromNavigationParameter, customParams);
        if (MicrosoftTeamsRecognitionService.isRunningWithinMicrosoftTeams()) {
            path.shift();
        }
        void this.router.navigate(path, parameters);
    }

    private buildNavigationExtras(addOpenFromNavigationParameter: boolean, customParams?: {
        [key: string]: unknown
    }): NavigationExtras | undefined {
        const currentLearnerId = this.getLearnerIdFromCurrentUrl();
        let queryParams: Params = {};

        if (addOpenFromNavigationParameter === true) {
            queryParams['openInConversation'] = 'true';
        }

        if (currentLearnerId !== undefined) {
            queryParams['learnerid'] = currentLearnerId;
        }

        if (customParams !== undefined) {
            queryParams = {...queryParams, ...customParams};
        }

        return Object.keys(queryParams).length > 0 ? {queryParams} : undefined;
    }

    private getLearnerIdFromCurrentUrl(): string | undefined {
        return this.activeRoute.snapshot.queryParamMap.get('learnerid') ?? undefined;
    }

    public shouldNavigateToUrlWhenOpeningResource(): boolean {
        return false;
    }

    public isRunningInWidget(): boolean {
        return false;
    }

    public isChatTabActive(): boolean {
        return this.router.url.split('?')[0] === '/learn';
    }

    public isMicromateResourceUrl(sourceUrl: string): boolean {
        return sourceUrl.startsWith(environment.learnUrl);
    }

    public navigateToInsightPopup(learningPackageId: string): void {
        this.navigate(['learn', 'lernfortschritt', learningPackageId, 'insights', 'details'], false);
    }

    public navigateToInsightPopupTab(learningPackageId: string, tab: InsightTabs, customParams?: {
        [key: string]: unknown
    }): void {
        this.navigate(['learn', 'lernfortschritt', learningPackageId, 'insights', tab], false, customParams);
    }

    public setTimePeriodModeForInsightPopupLearningHistoryTab(learningPackageId: string, timePeriodMode: TimePeriodMode): void {
        this.navigateToInsightPopupTab(learningPackageId, InsightTabs.LEARNING_HISTORY, {timePeriodMode: timePeriodMode});
    }
}
